import { Rule } from '@/enums/Rule'
import type { IRuleWithPayload } from '@/interfaces/IRuleWithPayload'
import dayjs from 'dayjs'
import * as EmailValidator from 'email-validator'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatBytes, isDefined } from './utils'

function _isValidMinDate(date: Date, minDate: string) {
  const minMilliseconds = new Date(minDate).getTime()
  if (isNaN(minMilliseconds) === false) {
    const milliseconds = date.getTime()
    if (milliseconds < minMilliseconds) {
      return false
    }
  } else {
    return false
  }
  return true
}

function _isValidMaxDate(date: Date, maxDate: string) {
  const maxMilliseconds = new Date(maxDate).getTime()
  if (isNaN(maxMilliseconds) === false) {
    const milliseconds = date.getTime()
    if (milliseconds > maxMilliseconds) {
      return false
    }
  } else {
    return false
  }
  return true
}

/**
 * Manages all rules related to user import and selection controls.
 * @returns
 */
export function useRulesManager() {
  const rules = ref<Map<Rule, Function>>(new Map<Rule, Function>())
  const i18n = useI18n({ useScope: 'global' })

  rules.value.set(Rule.ValidActivationCode, (value: string) => {
    if (!value) return true
    const activationCodePattern = /^[a-zA-Z0-9]{4}(-[a-zA-Z0-9]{4}){7}$/
    if (activationCodePattern.test(value)) {
      return true
    }
    return i18n.t('validation.invalidActivationCode')
  })

  rules.value.set(Rule.DuplicateString, (value: string, payload: Array<string> = []) => {
    if (
      !payload
        .map((item) => {
          return item.toLowerCase()
        })
        .includes(value?.toLowerCase())
    ) {
      return true
    }
    return i18n.t('validation.duplicateOption')
  })

  rules.value.set(Rule.ValidEmail, (value: string | undefined) => {
    if (!value) return true
    const isValid = EmailValidator.validate(value)
    if (isValid) {
      return true
    }
    return i18n.t('validation.invalidEmail')
  })

  rules.value.set(Rule.ValidNumber, (value: number, payload: Record<string, any> = {}) => {
    if (value === undefined || value === null) {
      return true
    }
    if (isNaN(value)) {
      return i18n.t('validation.invalidNumber')
    }
    if (
      isDefined(payload.min) &&
      isDefined(payload.max) &&
      (value < payload.min || value > payload.max)
    ) {
      return i18n.t('validation.invalidMinMax', { min: payload.min, max: payload.max })
    } else if (isDefined(payload.max) && value > payload.max) {
      return i18n.t('validation.invalidMax', { max: payload.max })
    } else if (isDefined(payload.min) && value < payload.min) {
      return i18n.t('validation.invalidMin', { min: payload.min })
    }
    return true
  })

  rules.value.set(
    Rule.MaxFileSize,
    (value: File | Array<File>, payload: Record<string, any> = {}) => {
      if (!isDefined(value) || (Array.isArray(value) && value.length === 0)) {
        return true
      }

      const fileSize = Array.isArray(value) ? value[0].size : value.size

      if (isDefined(payload.max) && fileSize > payload.max) {
        const maxSize = formatBytes(payload.max)
        return i18n.t('validation.invalidFileSize', { maxSize })
      }
      return true
    }
  )

  rules.value.set(Rule.Required, (value: string) => {
    if (isDefined(value)) {
      return true
    }
    return i18n.t('validation.valueRequired')
  })

  rules.value.set(Rule.ValidDate, (value: string, payload: Record<string, any> = {}) => {
    if (!value) return true
    const date = new Date(value)
    if (isNaN(date.getTime()) === true) {
      return i18n.t('validation.invalidDate')
    }

    if (isDefined(payload.min)) {
      if (!_isValidMinDate(date, payload.min)) {
        return i18n.t('validation.invalidMinDate')
      }
    }

    if (isDefined(payload.max)) {
      if (!_isValidMaxDate(date, payload.max)) {
        return i18n.t('validation.invalidMaxDate')
      }
    }
    return true
  })

  rules.value.set(Rule.ValidFutureTime, (value: any) => {
    const timeValue = value.modelRef.value
    const dateValue = value.dateRef.value

    // Don't test if we are missing a date or time value
    if (!isDefined(timeValue) || !isDefined(dateValue)) {
      return true
    }

    const now = new Date()

    const currentValue = new Date(
      `${dayjs(dateValue).format('MM/DD/YYYY')} ${timeValue.toTimeString(false)}`
    )

    if (currentValue >= now) {
      return true
    }
    return i18n.t('validation.timeInPast')
  })

  /**
   * Returns a set of rules.
   * @param keys A single rule, a single rule with a payload or any array of rules.
   * @returns
   */
  const getRules = (keys: Rule | IRuleWithPayload | Array<Rule | IRuleWithPayload>) => {
    if (!Array.isArray(keys)) {
      keys = [keys]
    }
    return keys.map((rule: Rule | IRuleWithPayload) => {
      if (typeof rule === 'string') {
        return rules.value.get(rule)
      }
      const foundRule = rules.value.get(rule.key)
      if (foundRule) {
        return (value: any) => {
          return foundRule(value, rule.payload)
        }
      }
    })
  }

  return {
    getRules
  }
}
